import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { notification } from 'antd';
import { getApiErrorMessage } from 'shared/utils';
import { useUpdateQualityMetric } from 'shared/hooks/quality-metric.hooks';
import ResourceManager from 'src/components/core/select-content/resource-manager.component';
import { useQualityMetricsManagementContext } from '../quality-metrics-management.context';

/**
 * `external-url` and `file` resources carry no `id`, so a resource can't be
 * identified by `id` alone. Match on the id when there is one, otherwise the
 * url, and fall back to the title.
 */
const isSameResource = (a, b) =>
  a.type === b.type && (a.id ? a.id === b.id : a.url ? a.url === b.url : a.title === b.title);

/**
 * Attach / detach a metric's resources. Unlike rotations there is no
 * per-resource endpoint — `PUT /:id` replaces the whole array — so each add and
 * remove sends the recomputed list.
 */
export function ManagementResourcesTab() {
  const { selectedMetric, lang } = useQualityMetricsManagementContext();
  const currentUserId = useSelector(state => state.auth.tokenDetails?.id);
  const [api, contextHolder] = notification.useNotification();
  const updateMetric = useUpdateQualityMetric();

  const resources = useMemo(() => selectedMetric?.resources ?? [], [selectedMetric?.resources]);

  const saveResources = useCallback(
    async (next, successMessage) => {
      try {
        await updateMetric.mutateAsync({ id: selectedMetric.id, payload: { resources: next } });
        api.success({ message: successMessage, placement: 'bottomRight' });
      } catch (error) {
        api.error({
          message: getApiErrorMessage(error) || lang.ERROR_GENERIC,
          placement: 'bottomRight',
        });
      }
    },
    [api, lang.ERROR_GENERIC, selectedMetric?.id, updateMetric],
  );

  const handleAdd = useCallback(
    // `SelectContent` composes the `{ type, id, title }` / `{ type: 'external-url', ... }`
    // payload; the server does not fill `createdBy`, so we do.
    payload =>
      saveResources([...resources, { ...payload, createdBy: currentUserId }], lang.RESOURCE_ADDED),
    [currentUserId, lang.RESOURCE_ADDED, resources, saveResources],
  );

  const handleRemove = useCallback(
    resource =>
      saveResources(
        resources.filter(existing => !isSameResource(existing, resource)),
        lang.RESOURCE_REMOVED,
      ),
    [lang.RESOURCE_REMOVED, resources, saveResources],
  );

  return (
    <ResourceManager
      resources={resources}
      canEdit
      notFoundText={lang.NO_RESOURCES}
      onAddResource={handleAdd}
      onRemoveResource={handleRemove}
      modalTitle={lang.ADD_RESOURCE}
      disabledResourceIds={resources.map(({ id }) => id).filter(Boolean)}
      contextHolder={contextHolder}
      lang={lang}
      closeOnAdd
    />
  );
}
